import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function createCustomIcon({ label, bg = "#2563eb", border = "#ffffff", textColor = "#ffffff", isLogo = false, logoUrl = "" }) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${bg};
          border: 3px solid ${border};
          box-shadow: 0 4px 14px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width: 28px; height: 28px; object-fit: contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${bg};
        border: 2px solid ${border};
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          font-family: Arial, sans-serif;
          line-height: 1;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const routeIcon = createCustomIcon({ label: "R", bg: "#2563eb", isLogo: true, logoUrl: logo });

export default function CreatePlace() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [routeName, setRouteName] = useState("");
  const [routeLocation, setRouteLocation] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
  }, []);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Fix for leaflet icon issue
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
      iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
      shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    });

    const map = L.map(mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    });

    mapInstanceRef.current = map;

    map.setView([20.5937, 78.9629], 5);

    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);

    // Add geocoder
    const geocoder = L.Control.geocoder({
      defaultMarkGeocode: false,
      position: 'topleft',
      placeholder: 'Search location...',
      errorMessage: 'Location not found',
    })
    .on("markgeocode", (e) => {
      const center = e.geocode.center;
      const locationName = e.geocode.name || "";

      if (e.geocode.bbox) {
        map.fitBounds(e.geocode.bbox);
      } else {
        map.setView(center, 16);
      }

      setLatLng(center.lat, center.lng, locationName);
      toast.success(`Location "${locationName}" found and pinned!`);
    })
    .addTo(map);

    geocoderRef.current = geocoder;

    map.on("click", (ev) => {
      geocodeLatLng(ev.latlng.lat, ev.latlng.lng);
    });

    const resizeMap = () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.invalidateSize(true);
      }
    };

    setTimeout(resizeMap, 100);
    setTimeout(resizeMap, 500);
    setTimeout(resizeMap, 1000);
    setTimeout(resizeMap, 1800);

    window.addEventListener("resize", resizeMap);

    return () => {
      window.removeEventListener("resize", resizeMap);
      map.remove();
      mapInstanceRef.current = null;
      geocoderRef.current = null;
      markerRef.current = null;
    };
  }, []);

  const geocodeLatLng = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      const locationName = data.display_name || "";

      setLatLng(lat, lng, locationName);
      
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setView([lat, lng], 16);
      }
      
      toast.success(`Location "${locationName}" pinned!`);
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      setLatLng(lat, lng, "Selected location");
    }
  };

  const setLatLng = (lat, lng, locationName) => {
    const lat7 = Number(lat).toFixed(7);
    const lng7 = Number(lng).toFixed(7);

    setLatitude(lat7);
    setLongitude(lng7);
    setRouteLocation(locationName || routeLocation);

    // Update marker
    if (mapInstanceRef.current) {
      if (markerRef.current) {
        mapInstanceRef.current.removeLayer(markerRef.current);
      }
      markerRef.current = L.marker([lat, lng], { icon: routeIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(`📍 ${locationName || "Selected location"}`)
        .openPopup();
    }
  };

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

 const handleSearchLocation = async () => {
  const query = routeLocation.trim();

  if (!query) {
    toast.error("Enter a location name first.");
    return;
  }

  try {
    toast.loading("Searching location...", { id: "location-search" });

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}&limit=1&addressdetails=1`
    );

    const results = await response.json();

    if (!Array.isArray(results) || results.length === 0) {
      toast.error("Location not found. Please try another name.", {
        id: "location-search",
      });
      return;
    }

    const place = results[0];
    const lat = Number(place.lat);
    const lng = Number(place.lon);
    const locationName = place.display_name || query;

    setLatLng(lat, lng, locationName);

    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lng], 16);
    }

    toast.success(`Location found and pinned!`, {
      id: "location-search",
    });
  } catch (error) {
    console.error("Search location error:", error);
    toast.error("Unable to search location.", {
      id: "location-search",
    });
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!routeName.trim()) {
      toast.error("Please enter a route name.");
      return;
    }

    if (!routeLocation.trim()) {
      toast.error("Please select a location on the map.");
      return;
    }

    if (!latitude || !longitude) {
      toast.error("Please pin a location on the map.");
      return;
    }

    try {
      setSaving(true);
      const token = getToken();

      const payload = {
        route_name: routeName.trim(),
        location: routeLocation.trim(),
        latitude: parseFloat(latitude),
        longitude: parseFloat(longitude),
      };

      const res = await fetch(api("/api/routes"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to create route");
      }

      toast.success("Route created successfully!");
      setTimeout(() => {
        navigate("/place_list");
      }, 1000);
    } catch (error) {
      console.error("Create route error:", error);
      toast.error(error.message || "Failed to create route.");
    } finally {
      setSaving(false);
    }
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading route creation...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="create-route"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              Create New Route
            </h1>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Define a new route by entering details and pinning it on the map
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/place_list")}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
          >
            Route List
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Form Section */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <div className="relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
            </div>

            <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-1 mt-3">
              Route Details
            </h2>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mb-5">
              Enter the route information and pin the location on the map
            </p>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Route Name <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <input
                    type="text"
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                    placeholder="Enter route name"
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />
                </div>

                <div>
                  <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                    Route Location <span className="text-[var(--red)] ml-0.5">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={routeLocation}
                      onChange={(e) => setRouteLocation(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSearchLocation();
                        }
                      }}
                      placeholder="Search location or click on map"
                      className="flex-1 rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                    />
                    <button
                      type="button"
                      onClick={handleSearchLocation}
                      className="border-0 rounded-xl px-4 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5 whitespace-nowrap"
                      style={{
                        background: "var(--btn-bg)",
                        color: "var(--btn-text)",
                        boxShadow: "0 4px 18px var(--accent-glow)",
                      }}
                    >
                      Search
                    </button>
                  </div>
                  <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                    Type a location name and click Search, or click directly on the map
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                      Latitude
                    </label>
                    <input
                      type="text"
                      value={latitude}
                      readOnly
                      placeholder="—"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                      Longitude
                    </label>
                    <input
                      type="text"
                      value={longitude}
                      readOnly
                      placeholder="—"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  style={{
                    background: "var(--btn-bg)",
                    color: "var(--btn-text)",
                    boxShadow: "0 4px 18px var(--accent-glow)",
                  }}
                >
                  {saving ? "Creating..." : "Create Route"}
                </button>
              </div>
            </form>

            <div className="mt-4 p-3 rounded-xl bg-[var(--surface)] border border-[var(--surface-border)]">
              <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-2">
                <span className="text-[var(--accent)]">💡</span>
                Tip: Click anywhere on the map to pin a location, or search for a specific place.
              </p>
            </div>
          </div>

          {/* Map Section */}
          <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-6 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
            <div className="relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
            </div>

            <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0 mb-1 mt-3">
              Route Map
            </h2>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mb-4">
              Click on the map to set the route location
            </p>

            <div className="w-full h-[500px] relative overflow-hidden rounded-2xl bg-[var(--surface)] border border-[var(--surface-border)]">
              <div
                ref={mapRef}
                id="create-route-leaflet-map"
                className="w-full h-[500px] block relative z-[1] rounded-2xl overflow-hidden bg-[var(--surface)]"
              />
            </div>

            <div className="mt-3 flex items-center gap-2 text-[11px] text-[var(--text-muted)]">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[var(--accent-faint)] text-[var(--accent)] text-[10px] font-extrabold">
                <span>📍</span> Click to pin
              </span>
              <span className="text-[var(--text-muted)]">·</span>
              <span>Use the search box on the map to find locations</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        #create-route-leaflet-map .leaflet-container,
        .leaflet-container {
          width: 100% !important;
          height: 500px !important;
          display: block !important;
          background: var(--surface) !important;
          z-index: 1 !important;
        }

        .leaflet-pane {
          z-index: 2 !important;
        }

        .leaflet-top,
        .leaflet-bottom {
          z-index: 10 !important;
        }

        .leaflet-control-container {
          z-index: 20 !important;
        }

        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }

        .leaflet-control-geocoder {
          border-radius: 12px !important;
          border: 1px solid var(--surface-border) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
          margin: 10px !important;
        }

        .leaflet-control-geocoder-form {
          display: flex !important;
          align-items: center !important;
        }

        .leaflet-control-geocoder input {
          background: var(--surface) !important;
          color: var(--text-primary) !important;
          padding: 8px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          outline: none !important;
          font-size: 13px !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          min-width: 200px !important;
        }

        .leaflet-control-geocoder input::placeholder {
          color: var(--text-faint) !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon {
          background: var(--btn-bg) !important;
          border-radius: 8px !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          margin-left: 4px !important;
          border: none !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon:before {
          content: "🔍" !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }

        .leaflet-popup-content-wrapper {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-radius: 12px !important;
          border: 1px solid var(--card-border) !important;
        }

        .leaflet-popup-tip {
          background: var(--card-bg) !important;
        }

        .leaflet-popup-content {
          color: var(--text-primary) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
        }

        .leaflet-control-zoom {
          margin: 10px !important;
        }

        .leaflet-control-zoom a {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-color: var(--surface-border) !important;
        }

        .leaflet-control-zoom a:hover {
          background: var(--accent-faint) !important;
        }
      `}</style>
    </Layout>
  );
}